import type { OpenSource } from '@parcel/analytics';
import { useNavigate, useRouter, useRouterState, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import type { AccountRow } from './mock-accounts.js';
import type { ProjectRow } from './projects-display.js';

export type RecordType = 'account' | 'contact' | 'project';

export interface RecordRef {
  type: RecordType;
  id: string;
}

export type RecordSeed = AccountRow | ProjectRow;

/** Travels in router history state, never the URL: absent on refresh and shared links. */
export interface RecordNavState {
  seed?: RecordSeed | null;
  source?: OpenSource;
  fromLabel?: string;
  // In-place hops since the record opened, and whether that open pushed a history entry.
  depth?: number;
  pushed?: boolean;
  // The view is expanded because of the default, not an explicit expand click.
  defaultExpanded?: boolean;
  /** The tab the record opens on (the Feed's Review contacts); a reload keeps it, a shared link opens on the first. */
  tab?: string;
}

export const RECORD_SEARCH_KEYS = ['record', 'full', 'follow'] as const;

// Fresh opens use the drawer; members can still expand explicitly or share full=1 links.
export const OPEN_EXPANDED_BY_DEFAULT = false;

const RECORD_TYPES: readonly RecordType[] = ['account', 'contact', 'project'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseRecordParam(value: unknown): RecordRef | null {
  if (typeof value !== 'string') return null;
  const separator = value.indexOf(':');
  if (separator < 0) return null;
  const type = value.slice(0, separator) as RecordType;
  const id = value.slice(separator + 1);
  if (!RECORD_TYPES.includes(type) || !UUID_RE.test(id)) return null;
  return { type, id };
}

export function formatRecordParam(ref: RecordRef): string {
  return `${ref.type}:${ref.id}`;
}

/** Root-route validateSearch: child routes inherit the result. */
export function validateRecordSearch(search: Record<string, unknown>): {
  record?: string;
  full?: 1;
  follow?: 1;
} {
  const ref = parseRecordParam(search.record);
  if (!ref) return {};
  // Number, not '1': the router would JSON-quote a numeric-looking string in the URL.
  const full = search.full === 1 || search.full === '1';
  const follow = search.follow === 1 || search.follow === '1';
  return {
    record: formatRecordParam(ref),
    ...(full ? { full: 1 as const } : {}),
    ...(follow ? { follow: 1 as const } : {}),
  };
}

type Search = Record<string, unknown>;

export function withRecord(prev: Search, ref: RecordRef): Search {
  // A dossier's follow belongs to the record it named, never to the next one opened.
  return { ...prev, record: formatRecordParam(ref), follow: undefined };
}

export function withoutRecord(prev: Search): Search {
  return { ...prev, record: undefined, full: undefined, follow: undefined };
}

/** The dossier flag leaves once the follow was applied, or found already in place. */
export function withoutFollow(prev: Search): Search {
  return { ...prev, follow: undefined };
}

export function withFull(prev: Search, on: boolean): Search {
  return { ...prev, full: on ? 1 : undefined };
}

/** Search for open(): a fresh open takes the default mode, an already-open record keeps its mode. */
export function withOpenedRecord(prev: Search, ref: RecordRef): Search {
  const next = withRecord(prev, ref);
  if (parseRecordParam(prev.record) || !OPEN_EXPANDED_BY_DEFAULT) return next;
  return withFull(next, true);
}

const EMPTY_NAV_STATE: RecordNavState = {};

export function openNavState(
  current: RecordNavState,
  currentRecord: RecordRef | null,
  opts: { seed?: RecordSeed | null; source?: OpenSource; replace?: boolean; tab?: string },
): RecordNavState {
  const base = {
    seed: opts.seed ?? null,
    source: opts.source,
    ...(opts.tab ? { tab: opts.tab } : {}),
  };
  if (!currentRecord) {
    const defaultExpanded = OPEN_EXPANDED_BY_DEFAULT;
    return { ...base, depth: 0, pushed: !opts.replace, defaultExpanded };
  }
  // Over an open record, a push (command palette) is one more hop; a replace (prev/next) is not.
  const depth = (current.depth ?? 0) + (opts.replace ? 0 : 1);
  const defaultExpanded = current.defaultExpanded ?? false;
  return { ...base, depth, pushed: current.pushed ?? false, defaultExpanded };
}

export function relatedNavState(
  current: RecordNavState,
  opts: { seed?: RecordSeed | null; fromLabel?: string },
): RecordNavState {
  return {
    seed: opts.seed ?? null,
    source: 'drawer_nav',
    fromLabel: opts.fromLabel,
    depth: (current.depth ?? 0) + 1,
    pushed: current.pushed ?? false,
    defaultExpanded: current.defaultExpanded ?? false,
  };
}

/** Entries to go back on close, and whether the landing entry still holds a record to replace. */
export function closeHistoryPlan(state: RecordNavState): { back: number; replace: boolean } {
  const depth = state.depth ?? 0;
  // A link-opened record owns no entry of its own: unwind its hops, then clear it in place.
  return state.pushed ? { back: depth + 1, replace: false } : { back: depth, replace: true };
}

export function useRecordDetail() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const router = useRouter();
  const navState = useRouterState({
    select: (state) =>
      (state.location.state as { recordDetail?: RecordNavState }).recordDetail ?? EMPTY_NAV_STATE,
  });

  const recordParam = typeof search.record === 'string' ? search.record : null;
  const record = React.useMemo(() => parseRecordParam(recordParam), [recordParam]);
  const expanded = record !== null && search.full === 1;
  const followIntent = record !== null && search.follow === 1;

  const open = React.useCallback(
    (
      ref: RecordRef,
      opts: {
        seed?: RecordSeed | null;
        source?: OpenSource;
        replace?: boolean;
        tab?: string;
        // Extra keys applied in the same navigation, so two updates cannot race.
        searchPatch?: Search;
      } = {},
    ) => {
      const recordDetail = openNavState(navState, record, opts);
      void navigate({
        to: '.',
        search: ((prev: Search) =>
          withOpenedRecord({ ...prev, ...opts.searchPatch }, ref)) as never,
        state: { recordDetail } as never,
        replace: opts.replace ?? false,
      });
    },
    [navState, navigate, record],
  );

  const navigateTo = React.useCallback(
    (ref: RecordRef, opts: { seed?: RecordSeed | null; fromLabel?: string } = {}) => {
      const recordDetail = relatedNavState(navState, opts);
      void navigate({
        to: '.',
        search: ((prev: Search) => withRecord(prev, ref)) as never,
        state: { recordDetail } as never,
      });
    },
    [navState, navigate],
  );

  // unwind: false clears in place, keeping search that was replaced onto the record's entry.
  const close = React.useCallback(
    (opts: { unwind?: boolean } = {}) => {
      const { back, replace } = closeHistoryPlan(navState);
      const clear = () => void navigate({ to: '.', search: withoutRecord as never, replace: true });
      if (back === 0 || opts.unwind === false) {
        clear();
        return;
      }
      // Going back drops every in-place hop, so Back never reopens a closed record.
      if (replace) {
        const unsubscribe = router.history.subscribe(() => {
          unsubscribe();
          clear();
        });
      }
      router.history.go(-back);
    },
    [navState, navigate, router],
  );

  const setFull = React.useCallback(
    (on: boolean) => {
      // state: true keeps the seed and back label across the mode toggle.
      void navigate({
        to: '.',
        search: ((prev: Search) => withFull(prev, on)) as never,
        state: true,
        replace: true,
      });
    },
    [navigate],
  );
  const expand = React.useCallback(() => setFull(true), [setFull]);
  const collapse = React.useCallback(() => setFull(false), [setFull]);
  const back = React.useCallback(() => router.history.back(), [router]);
  const consumeFollowIntent = React.useCallback(() => {
    // state: true keeps the seed and back label across the strip, as setFull does.
    void navigate({ to: '.', search: withoutFollow as never, state: true, replace: true });
  }, [navigate]);

  return {
    record,
    expanded,
    followIntent,
    consumeFollowIntent,
    navState,
    open,
    navigateTo,
    close,
    expand,
    collapse,
    back,
  };
}
